import React, { useState } from "react";

/**
 * ShiftLog — "What the last shifts learned" (Getting Good, Nov 2026).
 *
 * The fast layer beside the profile: shift notes one team member offered to
 * the next, newest first, each marked as a shift note — dated and
 * attributed, not a record — and gone after 14 days unless it was added to
 * the profile. Only where the agency has turned shift notes on.
 *
 * On someone else's entry, one tap: "Helpful" (Nov 2; was "I used this") —
 * whether she acted on it or it simply shaped how she saw the day. It
 * becomes "Helpful to you". The author sees who found it helpful; the
 * coordinator sees how many. Nobody here sees counts of anyone.
 *
 * `data` is GET /notes/shift-log: {entries}. With nothing to show — no
 * entries, or not loaded yet — it draws nothing at all (Nov 2): an empty
 * card is only clutter.
 */
export const SHIFT_LOG_COPY = {
  title: "What the last shifts learned",
  use: "Helpful",
  used: "Helpful to you",
};

export function dayWord(iso) {
  if (!iso) return "";
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

export function entryMeta(e, poss) {
  return e.in_profile
    ? `${e.author} · ${dayWord(e.created_at)} · now in ${poss} profile`
    : `${e.author} · ${dayWord(e.created_at)} · shift note`;
}

export default function ShiftLog({ clientId, poss = "their", data }) {
  const [used, setUsed] = useState({});
  const [error, setError] = useState(null);
  const entries = data?.entries || [];

  async function use(id) {
    setError(null);
    setUsed(u => ({ ...u, [id]: true }));
    try {
      const r = await fetch(`/api/clients/${clientId}/notes/${id}/use`, { method: "POST" });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't save that.");
      }
    } catch (e) {
      setUsed(u => ({ ...u, [id]: false }));
      setError(e.message);
    }
  }

  if (entries.length === 0) return null;

  return (
    <section style={s.card} aria-label={SHIFT_LOG_COPY.title}>
      <h2 style={s.title}>{SHIFT_LOG_COPY.title}</h2>
      <ul style={s.list}>
          {entries.map(e => {
            const mineUsed = used[e.id] ?? e.used_by_me;
            return (
              <li key={e.id} style={s.item}>
                <p style={s.meta}>{entryMeta(e, poss)}</p>
                <p style={s.text}>{e.text}</p>
                {!e.mine && (
                  <button type="button" style={{ ...s.use, ...(mineUsed ? s.usedOn : {}) }}
                          disabled={!!mineUsed} aria-pressed={!!mineUsed} onClick={() => use(e.id)}>
                    {mineUsed ? SHIFT_LOG_COPY.used : SHIFT_LOG_COPY.use}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      {error && <p style={s.error}>{error}</p>}
    </section>
  );
}

const s = {
  card:   { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-3) var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  title:  { fontSize: "var(--text-xs)", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-tertiary)", fontWeight: "var(--weight-semibold)", margin: 0 },
  list:   { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  item:   { display: "flex", flexDirection: "column", gap: 4, paddingTop: "var(--space-2)", borderTop: "1px solid var(--color-border)" },
  meta:   { margin: 0, fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  text:   { margin: 0, fontFamily: "var(--font-serif)", fontSize: "var(--text-base)", lineHeight: 1.45, color: "var(--color-text-primary)" },
  use:    { alignSelf: "flex-start", font: "inherit", fontSize: "var(--text-sm)", minHeight: 32, padding: "0 12px", borderRadius: 999, border: "1.5px solid var(--color-border-strong)", background: "var(--color-surface)", color: "var(--color-text-primary)", cursor: "pointer" },
  usedOn: { border: "1.5px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-accent)", cursor: "default" },
  error:  { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-critical)" },
};
